import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { fuzzyFilter } from '../../../../../common/utils/filters/fuzzy-match';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters/get-products-3p';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

/**
 * `workspaceName` filter — maps to `teamsFilters.workspaceName` in the AGG GraphQL schema.
 * Filters Microsoft Teams search results by workspace (team) name.
 *
 * The internal filter id, URL key, folder, and AGG wire field are all `workspaceName`
 * (per the naming convention agreed with the backend — PR 7086 in alexander/xpsearch-aggregator).
 *
 * Team name options are hardcoded for now; real fetch will be wired in a follow-up PR
 * once the backend endpoint is available.
 * Gated behind `rovo_fps_show_workspace_name_filter` (frontend) and
 * `xpsearch-aggregator_teams_team_name_filter` (backend).
 */

const products3PFromConfig = getProducts3PFromConfigForFilter('workspaceName');

/**
 * Session-level cache: maps cloudId → in-flight or resolved promise of workspace options.
 * This deduplicates concurrent and repeated calls (e.g. queryParams.fetcher + initialOptions.fetcher
 * both fire on page load when a team is pre-selected via URL param).
 * The cache is module-level so it persists for the browser session but resets on page navigation.
 */
const workspaceOptionsCache = new Map<string, Promise<SelectFilterOption[]>>();

/** Clears the workspace options cache. Exported for use in tests only. */
export const clearWorkspaceOptionsCache = (): void => workspaceOptionsCache.clear();

// TODO(SPVSI-1137): Replace HARDCODED_WORKSPACES with the real AGG fetch once
// `thirdPartyMSTeamsWorkspaces` is implemented in xpsearch-aggregator (alexander repo).
// The real implementation (doFetchMSTeamsWorkspaces below) is already wired up and ready —
// just swap the body back once the backend endpoint is deployed and gated.
// Backend tracking: https://bitbucket.org/atlassian/alexander/pull-requests/7086
const HARDCODED_WORKSPACES = ['Engineering', 'Design', 'Product', 'Marketing', 'Sales'];

async function doFetchMSTeamsWorkspaces(
	config: CloudConfig,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_cloudId: string,
): Promise<SelectFilterOption[]> {
	return HARDCODED_WORKSPACES.map((name, index) => ({
		value: name,
		trackingKey: `hardcoded-workspace-${index}`,
		queryParamValue: name,
		label: name,
		isDefault: false,
		isSelected: false,
		isSuggested: false,
		product: config.product,
	}));
}

const fetchWorkspaceOptions = (config: CloudConfig): Promise<SelectFilterOption[]> => {
	const { cloudId } = config;
	if (!cloudId) {
		return Promise.resolve([]);
	}
	// Return cached promise (handles both in-flight dedup and session-level caching).
	// If the fetch returns an empty result or rejects, remove the cache entry so a
	// subsequent open/retry can attempt a fresh fetch rather than returning stale empty results.
	if (!workspaceOptionsCache.has(cloudId)) {
		const promise = doFetchMSTeamsWorkspaces(config, cloudId);
		workspaceOptionsCache.set(cloudId, promise);
		promise
			.then((workspaces) => {
				if (workspaces.length === 0) {
					workspaceOptionsCache.delete(cloudId);
				}
			})
			.catch(() => {
				workspaceOptionsCache.delete(cloudId);
			});
	}
	return workspaceOptionsCache.get(cloudId)!;
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	// Hydrate selected team names from URL params by client-side filtering
	// the full fetched list — no extra API call needed.
	const allOptions = await fetchWorkspaceOptions(config);
	const selected = new Set(queryParams);
	return allOptions.filter((option) => selected.has(option.queryParamValue));
};

const lookupWorkspaces = async (
	query: string,
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchWorkspaceOptions(config);
	if (!query) {
		return allOptions;
	}
	return fuzzyFilter(query, allOptions, (option) => option.label);
};

export const filter: CreatedSelectFilter<'workspaceName'> = createMultiSelectFilter({
	id: 'workspaceName',
	products: products3PFromConfig,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'workspaceName',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchWorkspaceOptions(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return lookupWorkspaces(query, config);
		},
	},
});
